import clsx from 'clsx';
import React, { useLayoutEffect, useRef, useState } from 'react';
import { Button } from './Button';
import { Avatar, Callout, Kbd, Reason } from './Feedback';
import { StatusLabel, type Tone } from './Status';
import { IconArrowDown, IconArrowRight } from './icons';

export type ThreadAuthorKind = 'guest' | 'arthur' | 'staff';
export type Delivery = 'queued' | 'sent' | 'delivered' | 'failed';

export interface ThreadMessage {
  id: string | number;
  direction: 'in' | 'out';
  author: { kind: ThreadAuthorKind; name?: string | null };
  body: string;
  /** ISO timestamp. */
  at: string;
  delivery?: Delivery | null;
  error?: string | null;
}

export const DELIVERY_TONE: Record<
  Delivery,
  { tone: Tone; hollow?: boolean; label: string }
> = {
  queued: { tone: 'neutral', hollow: true, label: 'Sending' },
  sent: { tone: 'neutral', hollow: true, label: 'Sent' },
  delivered: { tone: 'success', label: 'Delivered' },
  failed: { tone: 'danger', label: 'Failed' },
};

/** Two turns by the same party within this window share one header. */
const COLLAPSE_MS = 5 * 60 * 1000;
/** Distance from the bottom that still counts as "reading the latest". */
const PIN_SLACK_PX = 24;

const timeOf = (iso: string) =>
  new Date(iso).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });

const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

/** "Today", "Yesterday", else "Sun, Sep 27" (with the year when it differs). */
export function dayLabel(iso: string, now = new Date()): string {
  const d = new Date(iso);
  if (sameDay(d, now)) return 'Today';
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (sameDay(d, yesterday)) return 'Yesterday';
  return d.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: d.getFullYear() === now.getFullYear() ? undefined : 'numeric',
  });
}

const DayDivider: React.FC<{ label: string }> = ({ label }) => (
  <li aria-hidden className="flex items-center gap-3 py-1">
    <span className="h-px flex-1 bg-v2-line/70" />
    <span className="rounded-full bg-v2-sunken px-2.5 py-0.5 text-v2-11 font-medium text-v2-fg-3">
      {label}
    </span>
    <span className="h-px flex-1 bg-v2-line/70" />
  </li>
);

/**
 * A live two-party thread as bubbles: the guest on the left on a sunken
 * tint, the store (Arthur or staff) on the right on an indigo tint. The
 * first turn of a run carries the avatar and a header (who · when); every
 * outbound turn carries its delivery state under the bubble. Arthur is the
 * brand mark, the guest their stable hue, staff neutral. Opens at the
 * bottom; new messages are followed only while the reader is at the bottom,
 * otherwise a "n new messages" button appears. Older messages prepended
 * through `lead` (e.g. "Load earlier") keep the reader's place.
 */
export const MessageThread: React.FC<{
  messages: ThreadMessage[];
  guestName?: string | null;
  /** Stable identity (customer id, phone) for the guest avatar hue. */
  guestIdentity?: string | number | null;
  /** Avatar glyph when the guest has no name. Omit it for a profile icon. */
  guestFallback?: string;
  className?: string;
  /** Rendered when there are no messages. */
  empty?: React.ReactNode;
  /** Rendered above the messages, e.g. a "Load earlier" button. */
  lead?: React.ReactNode;
}> = ({
  messages,
  guestName,
  guestIdentity,
  guestFallback,
  className,
  empty,
  lead,
}) => {
  const scroller = useRef<HTMLDivElement>(null);
  const pinned = useRef(true);
  const [unseen, setUnseen] = useState(0);
  const seen = useRef<{
    first?: ThreadMessage['id'];
    last?: ThreadMessage['id'];
    lastAt?: string;
    height: number;
  }>({ height: 0 });

  const scrollToBottom = (behavior: ScrollBehavior = 'auto') => {
    const el = scroller.current;
    if (el) el.scrollTo?.({ top: el.scrollHeight, behavior });
    pinned.current = true;
    setUnseen(0);
  };

  const onScroll = () => {
    const el = scroller.current;
    if (!el) return;
    const atBottom =
      el.scrollHeight - el.scrollTop - el.clientHeight < PIN_SLACK_PX;
    pinned.current = atBottom;
    if (atBottom) setUnseen(0);
  };

  useLayoutEffect(() => {
    const el = scroller.current;
    const prev = seen.current;
    const first = messages[0]?.id;
    const lastMessage = messages[messages.length - 1];
    const last = lastMessage?.id;

    if (prev.last === undefined) {
      if (last !== undefined) scrollToBottom();
    } else if (last === prev.last) {
      if (first !== prev.first && el) {
        el.scrollTop += el.scrollHeight - prev.height;
      }
    } else {
      const added = messages.filter((m) => !prev.lastAt || m.at > prev.lastAt);
      if (pinned.current || added.some((m) => m.direction === 'out')) {
        scrollToBottom('smooth');
      } else if (added.length > 0) {
        setUnseen((n) => n + added.length);
      }
    }
    seen.current = {
      first,
      last,
      lastAt: lastMessage?.at,
      height: el?.scrollHeight ?? 0,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  const guestLabel = guestName || 'Guest';

  return (
    <div className={clsx('relative flex min-h-0 flex-col', className)}>
      <div
        ref={scroller}
        onScroll={onScroll}
        className="v2-scroll min-h-0 flex-1 overflow-y-auto px-4 py-4 lg:px-6"
      >
        {lead}
        {messages.length === 0 ? (
          (empty ?? null)
        ) : (
          <ol className="flex flex-col gap-3" aria-label="Messages">
            {messages.map((m, i) => {
              const prev = messages[i - 1];
              const newDay = !prev || dayLabel(prev.at) !== dayLabel(m.at);
              const sameAuthor =
                !!prev &&
                !newDay &&
                prev.author.kind === m.author.kind &&
                prev.author.name === m.author.name &&
                new Date(m.at).getTime() - new Date(prev.at).getTime() <
                  COLLAPSE_MS;
              const name =
                m.author.kind === 'arthur'
                  ? 'Arthur'
                  : m.author.kind === 'guest'
                    ? guestLabel
                    : m.author.name || 'Staff';
              const out = m.direction === 'out';
              const delivery =
                out && m.delivery ? DELIVERY_TONE[m.delivery] : null;
              return (
                <React.Fragment key={m.id}>
                  {newDay && <DayDivider label={dayLabel(m.at)} />}
                  <li
                    className={clsx(
                      'flex gap-2.5',
                      out && 'flex-row-reverse',
                      sameAuthor && '-mt-2',
                    )}
                  >
                    <span className="w-7 shrink-0 pt-5">
                      {!sameAuthor &&
                        (m.author.kind === 'arthur' ? (
                          <Avatar arthur size="sm" />
                        ) : m.author.kind === 'guest' ? (
                          <Avatar
                            name={guestName}
                            identity={guestIdentity ?? guestName}
                            fallback={guestFallback}
                            hue="auto"
                            size="sm"
                          />
                        ) : (
                          <Avatar name={m.author.name} fallback="S" size="sm" />
                        ))}
                    </span>
                    <div
                      className={clsx(
                        'flex min-w-0 max-w-[min(78%,560px)] flex-col',
                        out ? 'items-end' : 'items-start',
                      )}
                    >
                      {sameAuthor ? (
                        <span className="sr-only">{name}</span>
                      ) : (
                        <div
                          className={clsx(
                            'mb-1 flex items-baseline gap-x-2 px-1 leading-4',
                            out && 'flex-row-reverse',
                          )}
                        >
                          <span
                            className={clsx(
                              'text-v2-12 font-semibold',
                              m.author.kind === 'arthur'
                                ? 'text-v2-accent'
                                : 'text-v2-fg',
                            )}
                          >
                            {name}
                          </span>
                          <time
                            dateTime={m.at}
                            className="v2-num text-v2-11 text-v2-fg-3"
                          >
                            {timeOf(m.at)}
                          </time>
                        </div>
                      )}
                      <p
                        className={clsx(
                          'whitespace-pre-wrap rounded-v2-lg px-3.5 py-2 text-v2-13 leading-5 text-v2-fg [overflow-wrap:anywhere]',
                          out ? 'bg-v2-accent/[0.11]' : 'bg-v2-sunken',
                          !sameAuthor &&
                            (out ? 'rounded-tr-v2-sm' : 'rounded-tl-v2-sm'),
                          m.delivery === 'failed' &&
                            'ring-1 ring-inset ring-v2-danger/40',
                        )}
                      >
                        {m.body}
                      </p>
                      {delivery && (
                        <StatusLabel
                          size="sm"
                          tone={delivery.tone}
                          hollow={delivery.hollow}
                          className={clsx(
                            'mt-1 px-1 !text-v2-11',
                            delivery.tone === 'danger'
                              ? '!text-v2-danger'
                              : '!text-v2-fg-3',
                          )}
                        >
                          {delivery.label}
                        </StatusLabel>
                      )}
                    </div>
                  </li>
                </React.Fragment>
              );
            })}
          </ol>
        )}
      </div>
      {unseen > 0 && (
        <div className="pointer-events-none absolute inset-x-0 bottom-3 flex justify-center">
          <Button
            size="sm"
            variant="secondary"
            className="pointer-events-auto shadow-v2-float"
            trailing={<IconArrowDown size={12} />}
            onClick={() => scrollToBottom('smooth')}
          >
            {unseen} new {unseen === 1 ? 'message' : 'messages'}
          </Button>
        </div>
      )}
    </div>
  );
};

/**
 * One rounded field: an auto-growing textarea (1–6 rows) over a footer with
 * the aside, the ⌘↵ hint and the one primary Send. The box takes the focus
 * ring so the field and its button read as one control. When it can't be
 * used it says why (`disabledReason`) instead of greying out. A failed send
 * keeps the draft and explains inline.
 */
export const Composer: React.FC<{
  value: string;
  onChange: (v: string) => void;
  onSend: () => void;
  sending?: boolean;
  disabledReason?: React.ReactNode;
  error?: string | null;
  onDismissError?: () => void;
  placeholder?: string;
  /** Left-hand line under the field, e.g. "Replying as …". */
  aside?: React.ReactNode;
  className?: string;
}> = ({
  value,
  onChange,
  onSend,
  sending,
  disabledReason,
  error,
  onDismissError,
  placeholder = 'Write a message…',
  aside,
  className,
}) => {
  const ref = useRef<HTMLTextAreaElement>(null);
  const disabled = !!disabledReason;

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const line = 20;
    el.style.height = 'auto';
    el.style.height = `${Math.min(line * 6 + 12, Math.max(line + 12, el.scrollHeight))}px`;
  }, [value]);

  const canSend = !disabled && !sending && value.trim().length > 0;

  return (
    <div
      className={clsx(
        'border-t border-v2-line bg-v2-surface px-4 py-3 lg:px-6',
        className,
      )}
    >
      {error && (
        <Callout tone="danger" className="mb-3" title="Message not sent">
          {error}
          {onDismissError && (
            <div className="mt-2">
              <Button size="sm" variant="ghost" onClick={onDismissError}>
                Dismiss
              </Button>
            </div>
          )}
        </Callout>
      )}
      {disabled ? (
        <Reason>{disabledReason}</Reason>
      ) : (
        <div className="rounded-v2-lg border border-v2-line-strong/80 bg-v2-surface transition-colors duration-100 hover:border-v2-line-strong focus-within:!border-v2-accent focus-within:ring-2 focus-within:ring-v2-accent/25">
          <textarea
            ref={ref}
            rows={1}
            value={value}
            placeholder={placeholder}
            aria-label="Message"
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                e.preventDefault();
                if (canSend) onSend();
              }
            }}
            className="block w-full resize-none rounded-t-v2-lg border-0 bg-transparent px-3 pb-1 pt-2 text-v2-13 leading-5 text-v2-fg shadow-none !outline-none placeholder:text-v2-fg-4 focus:ring-0"
          />
          <div className="flex items-center gap-3 pb-2 pl-3 pr-2">
            <span className="min-w-0 flex-1 truncate text-v2-11 text-v2-fg-3">
              {aside}
            </span>
            <span className="hidden shrink-0 text-v2-11 text-v2-fg-4 sm:inline">
              <Kbd>⌘</Kbd> <Kbd>↵</Kbd> to send
            </span>
            <Button
              size="sm"
              variant="primary"
              disabled={!canSend}
              loading={sending}
              onClick={onSend}
              trailing={sending ? undefined : <IconArrowRight size={12} />}
              className="shrink-0"
            >
              Send
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
